import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { TranslationService } from '@rocheglobal/component-library/src/services/translation/index';
import { TrackingService } from '@rocheglobal/component-library/src/services/tracking/index';
import { Props } from '../types';
import { blokToComponent } from '../helpers';
import { NavigationService, StringService as StringHelper } from '../../services';
import { MunchkinMarketo } from './munchkin-marketo';

// Properly type Window extensions
declare global {
  interface Window {
    MktoForms2: {
      loadForm: (
        baseUrl: string,
        munchkinId: string,
        formId: number,
        callback: (form?: unknown) => void
      ) => void;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    oneDataLayer: any[];
  }
}

// Form tracking events - these could be moved to a constants file if used elsewhere
const formEvents = {
  START: 'formStart',
  SUCCESS: 'formSuccess',
  ERROR: 'formError',
};

// Simple implementation of form tracking for Marketo with proper typing
const pushToDataLayer = (data: Record<string, unknown>): void => {
  if (typeof window !== 'undefined') {
    window.oneDataLayer = window.oneDataLayer || [];
    window.oneDataLayer.push(data);
  }
};

// Match with regular form implementation - using form type from props
const trackMarketoFormStart = (formType: string): void => {
  pushToDataLayer({
    event: formEvents.START,
    timestamp: Math.floor(Date.now() / 1000),
    content: {
      form: {
        name: '',
        type: formType,
        step: '',
        stepsTotal: '',
      },
    },
  });
};

const trackMarketoFormSuccess = (formType: string): void => {
  pushToDataLayer({
    event: formEvents.SUCCESS,
    timestamp: Math.floor(Date.now() / 1000),
    content: {
      form: {
        name: '',
        type: formType,
        step: '',
        stepsTotal: '',
      },
    },
  });
};

const trackMarketoFormError = (formType: string, error: string): void => {
  pushToDataLayer({
    event: formEvents.ERROR,
    timestamp: Math.floor(Date.now() / 1000),
    content: {
      form: {
        name: '',
        type: formType,
        step: '',
        stepsTotal: '',
        error,
      },
    },
  });
};

// Proper typing for Marketo form
interface MarketoForm {
  onRender?: (callback: () => void) => void;
  onValidate: (callback: (isValid: boolean) => void) => void;
  onSubmit: (callback: () => void) => void;
  onSuccess: (callback: () => boolean) => void;
}

type MarketoFormInfo = {
  scriptSourceUrl: string;
  marketoFormId: string;
  marketoUrl: string;
  munchkinId: string;
  formId: number;
} | null;

type FormMessagesTranslation = {
  translations: {
    successfulSubmissionMessage: string;
    errorSubmissionMessage: string;
  }
};

enum MarketoAnalytic {
  LOAD = 'marketo_load',
  LOAD_FAILED = 'marketo_load_failed',
  SUBMIT = 'marketo_submit',
  VALIDATE = 'marketo_validate',
  VALIDATION_FAILED = 'marketo_validation_failed',
  SUCCESS = 'marketo_success',
  LEAD_TRACKING = 'marketo_lead_tracking'
}

// Moved outside component for better reuse
function generateMarketoFormInfo(
  marketoProperty: string,
  marketoUrl: string,
  marketoId: number,
): MarketoFormInfo {
  const scriptSourceUrl = `${marketoUrl}/js/forms2/js/forms2.min.js`;
  const marketoFormId = `mktoForm_${marketoId}`;
  const munchkinId = marketoProperty;
  const formId = marketoId;

  if ([scriptSourceUrl, marketoFormId, munchkinId, formId]
    .some((variable) => typeof variable === 'undefined')) {
    return null;
  }

  return {
    scriptSourceUrl,
    marketoFormId,
    marketoUrl,
    munchkinId,
    formId,
  };
}

export const Marketo = ({
  blok, slot, getComponent,
}: Props): JSX.Element => {
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const [formMessagesTranslation, setFormMessagesTranslation] = useState<
    FormMessagesTranslation | undefined
  >(undefined);

  // Refs to persist state across renders
  const formStartTrackedRef = useRef<boolean>(false);
  const munchkinIdRef = useRef<string | null>(null);
  const formIdRef = useRef<string | null>(activeFormId);
  const eventListenersAttachedRef = useRef<boolean>(false);

  // Default form type to 'marketo' for tracking
  const formType = blok?.form_type || 'marketo';

  useEffect(() => {
    formIdRef.current = activeFormId;
    // Reset form tracking state when form ID changes
    formStartTrackedRef.current = false;
    eventListenersAttachedRef.current = false;
  }, [activeFormId]);

  // Memoize trackAnalytics to prevent unnecessary re-renders
  const trackAnalytics = useCallback((
    event: string,
    additionalParameters?: Record<string, string>,
  ): void => {
    TrackingService.push({
      event,
      component_name: 'marketo',
      additionalParameters: {
        form_id: formIdRef.current,
        munchkin_id: munchkinIdRef.current,
        form_type: formType,
        ...additionalParameters,
      },
    });
  }, [formType]);

  // Memoize handleFormStart to prevent unnecessary re-renders
  const handleFormStart = useCallback((): void => {
    // Use ref to check if already tracked
    if (formStartTrackedRef.current || !formIdRef.current) return;

    trackMarketoFormStart(formType);

    // Use ref to set tracking status
    formStartTrackedRef.current = true;
  }, [formType]);

  // Setup form field listeners to detect user interaction
  const setupFormInteractionListeners = useCallback((): void => {
    if (eventListenersAttachedRef.current || !formIdRef.current) return;

    const formElement = document.getElementById(formIdRef.current);
    if (!formElement) return;

    // Track form start only when users actually input data
    const textInputs = formElement.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="number"], textarea');
    textInputs.forEach((input) => {
      input.addEventListener('input', () => {
        // Only track if there's actual text in the field
        const { value } = (input as HTMLInputElement);
        if (value && value.trim() !== '') {
          handleFormStart();
        }
      }, { once: true });
    });

    // For select elements, track on change
    const selectInputs = formElement.querySelectorAll('select');
    selectInputs.forEach((select) => {
      select.addEventListener('change', () => {
        handleFormStart();
      }, { once: true });
    });

    // For checkboxes and radio buttons, track on change
    const checkboxInputs = formElement.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    checkboxInputs.forEach((input) => {
      input.addEventListener('change', () => {
        handleFormStart();
      }, { once: true });
    });

    eventListenersAttachedRef.current = true;
  }, [handleFormStart]);

  // Fetch translations for form messages
  const fetchFormMessagesTranslation = async (): Promise<FormMessagesTranslation> => {
    const translations = {
      translations: {
        successfulSubmissionMessage: await TranslationService.translate('form submitted successfully'),
        errorSubmissionMessage: await TranslationService.translate('an error occurred while submitting the form'),
      },
    };
    return translations;
  };

  // Handle successful form submission
  const handleSuccessfulFormSubmission = useCallback(async (): Promise<void> => {
    const redirectDelay = 2500;
    const isExternalURL = blok?.link?.linktype === 'url';
    const url = blok?.link?.story?.url || blok?.link?.url;

    trackMarketoFormSuccess(formType);

    // TODO: Remove this timeout and replace with actual form submission success handling
    setTimeout(() => {
      setIsSubmissionSuccessful(true);
      setIsSubmissionError(false);
    }, 500);

    if (!url || url === '') return;
    if (isExternalURL && !NavigationService.isWhitelistedDomain(url)) return;

    // TODO: fix this to work with the new navigation service
    setTimeout(() => {
      const redirectUrl = isExternalURL ? url : `/${url}`.replace('//', '/');
      window.location.replace(redirectUrl);
    }, redirectDelay);
  }, [blok?.link, formType]);

  // Form validation and event handling setup
  const handleFormValidation = useCallback((form: MarketoForm | undefined): void => {
    if (!form) {
      setIsLoadingError(true);
      trackAnalytics(MarketoAnalytic.LOAD_FAILED);
      return;
    }

    trackAnalytics(MarketoAnalytic.LOAD);

    // Set up form tracking - safely check if onRender exists
    if (form.onRender) {
      form.onRender(() => {
        setTimeout(() => {
          setupFormInteractionListeners();
        }, 500);
      });
    } else {
      // Fallback for when onRender isn't available
      setTimeout(() => {
        setupFormInteractionListeners();
      }, 1000);
    }

    form.onValidate((isValid: boolean) => {
      trackAnalytics(MarketoAnalytic.VALIDATE);

      if (!isValid) {
        setIsSubmissionError(true);
        trackAnalytics(MarketoAnalytic.VALIDATION_FAILED);

        // Call handleFormStart before sending error to ensure formStart is tracked
        handleFormStart();
        trackMarketoFormError(formType, 'validation_failed');
      }
    });

    form.onSubmit(() => {
      // Ensure form start is tracked before submit
      handleFormStart();
      trackAnalytics(MarketoAnalytic.SUBMIT);
    });

    form.onSuccess(() => {
      // Ensure form start is tracked before success
      handleFormStart();
      trackAnalytics(MarketoAnalytic.SUCCESS);
      handleSuccessfulFormSubmission();
      return false;
    });
  }, [
    trackAnalytics,
    setupFormInteractionListeners,
    handleFormStart,
    formType,
    handleSuccessfulFormSubmission,
  ]);

  // Load the Marketo form
  const loadMarketoForm = useCallback(
    (marketoUrl: string, munchkinId: string, formId: number): void => {
      if (!window.MktoForms2) {
        setIsSubmissionError(true);
        return;
      }

      window.MktoForms2.loadForm(marketoUrl, munchkinId, formId, (form) => {
        handleFormValidation(form as MarketoForm);
      });
    }, [handleFormValidation]);

  // Create script tag and append to document
  const createScriptAndAppendToBody = useCallback((
    scriptSourceUrl: string,
    marketoUrl: string,
    munchkinId: string,
    formId: number,
  ): HTMLScriptElement => {
    const script = document.createElement('script');
    script.src = scriptSourceUrl;
    script.async = true;
    script.onload = () => loadMarketoForm(marketoUrl, munchkinId, formId);
    document.body.appendChild(script);
    return script;
  }, [loadMarketoForm]);

  // Main effect to initialize the form
  useEffect(() => {
    const fetchTranslations = async (): Promise<void> => {
      try {
        setFormMessagesTranslation(await fetchFormMessagesTranslation());
      } catch (translationError) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch translations:', translationError);
      }
    };

    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      marketo_property, marketo_url, form_id,
    } = blok;
    const marketoFormInfo = generateMarketoFormInfo(marketo_property, marketo_url, form_id);
    if (!marketoFormInfo) {
      setIsLoadingError(true);
      return;
    }

    fetchTranslations();

    const {
      scriptSourceUrl, marketoFormId, marketoUrl, munchkinId, formId,
    } = marketoFormInfo;
    munchkinIdRef.current = munchkinId;
    setActiveFormId(marketoFormId);

    let script: HTMLScriptElement | null = null;
    if (!window.MktoForms2) {
      script = createScriptAndAppendToBody(scriptSourceUrl, marketoUrl, munchkinId, formId);
    } else {
      loadMarketoForm(marketoUrl, munchkinId, formId);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (script) document.body.removeChild(script);
    };
  }, [blok, createScriptAndAppendToBody, loadMarketoForm]);

  // Track lead acquisition
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const origin = params.get('mkt_tok');

    trackAnalytics(MarketoAnalytic.LEAD_TRACKING, { lead_origin: origin || '' });
  }, [activeFormId, trackAnalytics]);

  return (
    <roche-marketo>
      <MunchkinMarketo />
      <form
        id={activeFormId || undefined}
        style={{ display: isSubmissionSuccessful ? 'none' : 'block' }}
      ></form>

      <br></br>

      {isSubmissionSuccessful && (
        blok.success?.[0]
          ? blokToComponent({ blok: blok.success[0], slot, getComponent })
          : <div>{StringHelper.capitalize(formMessagesTranslation
            ?.translations?.successfulSubmissionMessage || '')}</div>
      )}

      {isSubmissionError && (
        blok?.error[0]
          ? blokToComponent({ blok: blok.error[0], slot, getComponent })
          : <div>{StringHelper.capitalize(formMessagesTranslation
            ?.translations?.errorSubmissionMessage || '')}</div>
      )}

      {isLoadingError && (
        <roche-editor-alert
          slot={slot}
          type='error'
          text='The embed snippet could not be parsed. Please review the embed code and try again.'
        />
      )}
    </roche-marketo>
  );
};
